import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Card, Logo, MonoLabel } from '../components/primitives/index.js';

export function ContestPreview({ id }: { id: string }) {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();

  const contest = useQuery({
    queryKey: ['contest', id],
    queryFn: () => api.contest(id),
    enabled: !!me.data,
  });

  if (me.isLoading || contest.isLoading) {
    return (
      <Shell>
        <div className="text-muted text-sm py-12 text-center">Se încarcă…</div>
      </Shell>
    );
  }

  const c = contest.data?.contest;
  if (!c || !c.pdf_key) {
    return (
      <Shell>
        <Card className="max-w-md mx-auto text-center">
          <h1 className="text-xl font-medium mb-2">PDF indisponibil</h1>
          <p className="text-muted text-sm mb-6">
            Plângerea nu a fost generată încă. Întoarce-te la pasul de analiză.
          </p>
          <Button variant="ghost" pill icon={false} onClick={() => setLocation(`/contest/${id}/result`)}>
            ← La rezultatul analizei
          </Button>
        </Card>
      </Shell>
    );
  }

  const fineLei = c.fine_amount_lei ?? 0;
  const draftUrl = api.pdfUrl(id, 'draft');

  return (
    <Shell>
      <div className="max-w-5xl mx-auto">
        <MonoLabel>Pasul 5 · previzualizare plângere</MonoLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-display-4 font-medium tracking-tight mt-3 mb-2">Plângerea ta e gata.</h1>
        <p className="text-muted text-base leading-relaxed max-w-2xl mb-6 md:mb-8">
          Vezi mai jos <strong className="text-fg">structura plângerii</strong> și motivele de
          nulitate identificate — citațiile exacte, raționamentul juridic și numerele de dosar ale
          spețelor sunt redactate până la plată. După cele 49 lei primești versiunea completă,
          fără watermark, gata de printat și depus la judecătorie.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* PDF embedded preview */}
          <div className="bg-card border border-line rounded-2xl overflow-hidden">
            <div className="bg-bg-2 px-4 py-3 border-b border-line flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📄</span>
                <div>
                  <div className="text-sm font-medium">plangere-DRAFT-{id.slice(-5)}.pdf</div>
                  <div className="text-xs text-muted">previzualizare · conținut juridic redactat</div>
                </div>
              </div>
              <a
                href={draftUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-mono uppercase tracking-wider text-muted hover:text-fg border border-line rounded-md px-3 py-1.5"
              >
                Deschide în tab nou ↗
              </a>
            </div>
            <iframe
              src={draftUrl}
              className="w-full h-[520px] sm:h-[640px] lg:h-[780px]"
              style={{ border: 0 }}
              title="Plângere DRAFT"
            />
          </div>

          {/* Order summary */}
          <div className="space-y-4">
            <Card>
              <MonoLabel>Sumar comandă</MonoLabel>
              <h2 className="mt-3 mb-4 text-2xl font-medium">Plângere completă</h2>

              <div className="space-y-2.5 text-sm py-3 border-y border-line">
                <Row label="Plângere PDF curată" value="—" />
                <Row label="Revizie avocat partener" value="inclus" />
                <Row label="Spețe similare atașate" value={`${c.analysis_json?.motive.reduce((a, m) => a + (m.spete_atasate?.length ?? 0), 0) ?? 0}`} />
                <Row label="Garanție 30 zile" value="✓" />
              </div>

              <div className="mt-4 flex justify-between items-baseline">
                <span className="text-base">Total</span>
                <div className="text-right">
                  <div className="text-3xl font-medium tracking-tight">49 lei</div>
                  <div className="text-xs text-muted">o singură plată, fără abonament</div>
                </div>
              </div>

              {fineLei > 0 && (
                <div className="mt-4 p-3 bg-accent-soft border border-accent/30 rounded-lg text-xs leading-relaxed">
                  <strong className="text-accent-dark">Economie potențială:</strong> dacă PV-ul e
                  anulat, eviți amenda de <strong>{fineLei.toLocaleString('ro-RO')} lei</strong>.
                  Multiplicator <strong className="text-accent-dark">{Math.round(fineLei / 49)}×</strong> peste cost.
                </div>
              )}

              <Button pill size="lg" disabled className="w-full mt-5">
                Plătește 49 lei
              </Button>
              <div className="text-[11px] text-muted text-center mt-2 leading-snug">
                Stripe checkout vine în Phase 3 (livrare săptămâna viitoare).
                <br />
                Până atunci poți descărca draft-ul pentru review.
              </div>
            </Card>

            <Card>
              <MonoLabel>Următorii pași după plată</MonoLabel>
              <ul className="mt-3 space-y-2 text-[13px] text-fg-2 leading-relaxed">
                <li className="flex gap-2">
                  <span className="text-accent-dark font-mono">01</span>
                  <span>Printezi plângerea (3 pagini A4)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-dark font-mono">02</span>
                  <span>Semnezi olograf și pui data la final</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-dark font-mono">03</span>
                  <span>Atașezi copia PV-ului contestat</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-dark font-mono">04</span>
                  <span>Depui la registratura judecătoriei competente</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-accent-dark font-mono">05</span>
                  <span>Te citează la termen — fără reprezentare</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:justify-between gap-3 sm:gap-0 border-t border-line pt-6">
          <Button
            variant="ghost"
            pill
            icon={false}
            onClick={() => setLocation(`/contest/${id}/result`)}
          >
            ← La motive de nulitate
          </Button>
          <a
            href={draftUrl}
            download
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-line text-fg hover:bg-bg-2 text-[15px]"
          >
            Descarcă draft-ul ↓
          </a>
        </div>
      </div>
    </Shell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-muted">{label}</span>
      <span className="font-mono text-xs">{value}</span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="px-4 md:px-6 py-6 md:py-10">{children}</main>
    </div>
  );
}
